/**
 * SubscriptionContext.js
 * React context that holds the live subscription bundle for the current
 * location.  Seeds from localStorage on mount, refreshes every 30 minutes,
 * and reacts to POS lifecycle events:
 *   pos:locationChanging  → clear bundle (show "Checking…" state)
 *   pos:subscriptionLoaded → re-read from localStorage (apply new bundle)
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { fetchSubscriptionBundle, resolveContext } from "../services/subscriptionService";

const CACHE_KEY = "storeSubscription";
const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

/* ── Context shape ─────────────────────────────────────── */
const SubscriptionContext = createContext({
  bundle: null,
  isLoaded: false,
  isLiveChecked: false,
  hasAddon: () => false,
  refresh: () => {},
});

/* ── Helper: read from localStorage cache ──────────────── */
function readCachedBundle() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/* ── Provider ─────────────────────────────────────────── */
export function SubscriptionProvider({ children }) {
  const [bundle, setBundle] = useState(() => readCachedBundle());
  const [isLoaded, setIsLoaded] = useState(() => !!readCachedBundle());
  const [isLiveChecked, setIsLiveChecked] = useState(false);
  const intervalRef = useRef(null);

  /**
   * Generation counter — increments on every pos:locationChanging event.
   * Any in-flight fetch that started before the last location change is
   * discarded when it resolves, preventing stale-location cache overwrites.
   */
  const generationRef = useRef(0);

  /* Fetch live bundle and write to cache */
  const fetchAndCache = useCallback(async () => {
    const myGeneration = generationRef.current;
    try {
      const { orgId, locId, accountId } = resolveContext();
      if (!orgId && !locId) return; // no location selected yet
      const result = await fetchSubscriptionBundle(accountId, orgId, locId);
      /* Discard result if a location change happened while we were fetching */
      if (generationRef.current !== myGeneration) return;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(result));
      } catch (_) {}
      setBundle(result);
      setIsLoaded(true);
      setIsLiveChecked(true);
    } catch (_) {
      if (generationRef.current !== myGeneration) return;
      // Network failure — keep whatever we had; mark both loaded and live-checked
      // so the SubscriptionGate is never left stuck on "Checking subscription…"
      setIsLoaded(true);
      setIsLiveChecked(true);
    }
  }, []);

  /* Manual refresh (exposed on context) */
  const refresh = useCallback(() => {
    fetchAndCache();
  }, [fetchAndCache]);

  /* Mount: seed from cache → then do a live fetch */
  useEffect(() => {
    const cached = readCachedBundle();
    if (cached) {
      setBundle(cached);
      setIsLoaded(true);
    }
    fetchAndCache();

    /* 30-minute background refresh */
    intervalRef.current = setInterval(fetchAndCache, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchAndCache]);

  /* Location-changing event: clear bundle immediately + bump generation */
  useEffect(() => {
    const onLocationChanging = () => {
      /* Increment generation so any in-flight fetch for the old location
         will discard its result when it eventually resolves. */
      generationRef.current += 1;
      setBundle(null);
      setIsLoaded(false);
      setIsLiveChecked(false);
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch (_) {}
    };
    window.addEventListener("pos:locationChanging", onLocationChanging);
    return () => window.removeEventListener("pos:locationChanging", onLocationChanging);
  }, []);

  /* Subscription-loaded event: re-read cache and mark live */
  useEffect(() => {
    const onSubscriptionLoaded = () => {
      const cached = readCachedBundle();
      if (cached) {
        setBundle(cached);
        setIsLoaded(true);
        setIsLiveChecked(true);
      } else {
        // Cache was absent or cleared at the moment the event fired
        // (e.g. rapid location switch + subscription refresh overlap).
        // Fall back to a live fetch so AddonGate is not left locked.
        fetchAndCache();
      }
    };
    window.addEventListener("pos:subscriptionLoaded", onSubscriptionLoaded);
    return () => window.removeEventListener("pos:subscriptionLoaded", onSubscriptionLoaded);
  }, [fetchAndCache]);

  /* hasAddon: fuzzy-match by packageName or addonId containing keyword */
  const hasAddon = useCallback(
    (keyword) => {
      if (!bundle?.addons?.length) return false;
      const kw = String(keyword).toLowerCase();
      return bundle.addons.some(
        (a) =>
          (a?.packageName && a.packageName.toLowerCase().includes(kw)) ||
          (a?.addonId && a.addonId.toLowerCase().includes(kw))
      );
    },
    [bundle]
  );

  return (
    <SubscriptionContext.Provider value={{ bundle, isLoaded, isLiveChecked, hasAddon, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/* ── Hook ─────────────────────────────────────────────── */
export function useSubscription() {
  return useContext(SubscriptionContext);
}

export default SubscriptionContext;
